import { useTranslation } from "react-i18next";
import { Button, Form, Input } from "antd";
import { useCallback, useEffect, useRef, useState } from "react";
import UploadToDiscuss from "../../../../../../../components/file-uploader/UploadToDiscuss";
import { useAuthStore } from "../../../../../../../stores/stores";

interface ReplyFormProps {
	mention: string;
	replyToId: number;
	uid: number;
	onCancel: () => void;
	onSubmit: (data: {
		replyToId: number;
		content: string;
		attachments?: File[];
	}) => void;
}

export default function ReplyForm({
	mention,
	replyToId,
	uid,
	onCancel,
	onSubmit
}: ReplyFormProps) {
  const { t } = useTranslation();
	const [form] = Form.useForm();
	const wrapperRef = useRef<HTMLDivElement>(null);
	const textAreaRef = useRef<any>(null);
	const [fileList, setFileList] = useState<any[]>([]);
	const [submitting, setSubmitting] = useState(false);
	const userInfo = useAuthStore((state) => state.user);

	useEffect(() => {
		form.setFieldsValue({ content: mention });
		requestAnimationFrame(() => {
			const textarea = textAreaRef.current?.resizableTextArea?.textArea as HTMLTextAreaElement;
			if (textarea) {
				textarea.focus();
				textarea.setSelectionRange(textarea.value.length, textarea.value.length);
			}
			if (wrapperRef.current) {
				wrapperRef.current.scrollIntoView({
					behavior: "smooth",
					block: "nearest"
				});
			}
		});
	}, [uid, mention, form]);

	const handleFinish = useCallback(async (values: { content: string }) => {
		const content = values.content.trim();
		if (!content && fileList.length === 0) return;

		setSubmitting(true);
		try {
			const attachments = fileList
				.filter(file => file.originFileObj)
				.map(file => file.originFileObj as File);

			await onSubmit({
				replyToId,
				content,
				attachments: attachments.length > 0 ? attachments : undefined
			});
			form.resetFields();
			setFileList([]);
		} finally {
			setSubmitting(false);
		}
	}, [fileList, form, onSubmit, replyToId]);

	const handleCancel = useCallback(() => {
		form.resetFields();
		setFileList([]);
		onCancel();
	}, [form, onCancel]);

	const handleFileChange = useCallback((newFileList: any[]) => {
		setFileList(newFileList);
	}, []);

	return (
		<div ref={wrapperRef} className="reply-form">
			<img
				className="reply-avatar"
				src={userInfo?.anhDaiDien ? userInfo?.anhDaiDien : `${process.env.PUBLIC_URL}/images/comment-avatar.png`}
				alt="Your avatar"
			/>

			<Form
				form={form}
				onFinish={handleFinish}
				style={{ flex: 1 }}
			>
				<Form.Item
					name="content"
					style={{ marginBottom: 0 }}
					rules={[
						{
							validator: (_, value) => {
								if (!value?.trim() && fileList.length === 0) {
									return Promise.reject('Vui lòng nhập nội dung hoặc đính kèm file');
								}
								return Promise.resolve();
							}
						}
					]}
				>
					<Input.TextArea
						ref={textAreaRef}
						id={`reply-textarea-${uid}`}
						autoSize={{ minRows: 1, maxRows: 6 }}
						placeholder={t("Viết phản hồi...", "Viết phản hồi...")}
						onPressEnter={(e) => {
							if (!e.shiftKey) {
								e.preventDefault();
								form.submit();
							}
						}}
					/>
				</Form.Item>

				<div className="reply-upload">
					<UploadToDiscuss
						fileData={[]}
						mode="edit"
						setAddAttachments={handleFileChange}
					/>
				</div>

				<div className="form-actions">
					<Button
						size="small"
						htmlType="button"
						onClick={handleCancel}
						disabled={submitting}
					>
						{t("Huỷ", "Huỷ")}
					</Button>
					<Button
						size="small"
						type="primary"
						htmlType="submit"
						loading={submitting}
					>
						{t("Trả lời", "Trả lời")}
					</Button>
				</div>
			</Form>
		</div>
	);
}